/**
 * Read-only book flip-through with arrows, page dots, and keyboard
 * navigation. Ported from the design prototype's spread-editor/preview.jsx.
 *
 * Shows the book the EXPORT produces, page-for-page: cover (with the
 * exporter's fallback when empty), the dedication page, the blank page
 * after the cover, interior spreads with physical folio numbers, and the
 * back cover — driven by the same project settings the PDF defaults to.
 */
import { useEffect, useState } from 'react';
import type { FormatPreset } from '@historyjki/shared';
import { Button } from '../ui/Button';
import { getCanvasAspect, getCanvasSizePt } from '../../utils/spreadGeometry';
import type { ComposeStore } from '../../hooks/useComposeStage';
import type { SpreadView } from '../../hooks/useComposeStage';
import { StaticSpread, type BookFont } from './SpreadCanvas';
import { IconBook, IconArrowLeft, IconArrowRight, IconX } from './icons';

interface PreviewOverlayProps {
  store: ComposeStore;
  bookFont: BookFont;
  onClose: () => void;
}

type PreviewItem =
  | { key: string; kind: 'spread'; spread: SpreadView; folios: [number, number] | null; label: string }
  | { key: string; kind: 'dedication'; text: string; label: string }
  | { key: string; kind: 'blank'; label: string };

/**
 * The export's page sequence as preview items. Mirrors buildBookPdf:
 * cover → dedication (if text) → blank (if enabled) → interiors (two
 * physical pages each, folios counting physical position) → back.
 */
function buildPreviewItems(store: ComposeStore): PreviewItem[] {
  const { project, spreads, labelFor } = store;
  const items: PreviewItem[] = [];
  let physicalPages = 0;

  const cover = spreads.find((s) => s.kind === 'cover');
  if (cover) {
    items.push({ key: cover.id, kind: 'spread', spread: cover, folios: null, label: 'Cover' });
  }
  physicalPages += 1; // the PDF always emits a cover page

  if (project?.dedication_text) {
    items.push({
      key: 'dedication',
      kind: 'dedication',
      text: project.dedication_text,
      label: 'Dedication',
    });
    physicalPages += 1;
  }
  if (project?.blank_page_after_cover) {
    items.push({ key: 'blank', kind: 'blank', label: 'Blank page' });
    physicalPages += 1;
  }

  for (const s of spreads) {
    if (s.kind !== 'spread') continue;
    items.push({
      key: s.id,
      kind: 'spread',
      spread: s,
      folios: [physicalPages + 1, physicalPages + 2],
      label: labelFor(s).label,
    });
    physicalPages += 2;
  }

  const back = spreads.find((s) => s.kind === 'back');
  if (back) {
    items.push({ key: back.id, kind: 'spread', spread: back, folios: null, label: 'Back cover' });
  }

  return items;
}

export function PreviewOverlay({ store, bookFont, onClose }: PreviewOverlayProps) {
  const { project, currentId } = store;
  const items = buildPreviewItems(store);

  // Open on the spread being edited, not always the cover.
  const [i, setI] = useState(() => {
    const at = items.findIndex((it) => it.kind === 'spread' && it.spread.id === currentId);
    return at >= 0 ? at : 0;
  });
  const go = (d: number) => setI((v) => Math.max(0, Math.min(items.length - 1, v + d)));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const item = items[Math.min(i, items.length - 1)];
  if (!item) return null;

  const formatPreset = project?.format_preset ?? null;
  const singleAspect = getCanvasAspect(formatPreset, 'cover');
  const title =
    item.kind === 'spread' && item.folios
      ? `${item.label} · pages ${item.folios[0]}–${item.folios[1]}`
      : item.label;

  return (
    <div className="preview" data-testid="preview-overlay">
      <div className="preview__bar">
        <span className="preview__crumb">
          <IconBook /> Preview
        </span>
        <span className="preview__label">{title}</span>
        <div className="preview__bar-spacer" />
        <Button variant="secondary" size="sm" icon={<IconX />} onClick={onClose}>
          Close preview
        </Button>
      </div>

      <div className="preview__main">
        <button
          type="button"
          className="preview__arrow"
          onClick={() => go(-1)}
          disabled={i === 0}
          aria-label="Previous"
        >
          <IconArrowLeft />
          <span className="preview__arrow__label">Previous</span>
        </button>
        <div className="preview__stage">
          <div className="spread-wrap">
            {item.kind === 'spread' ? (
              <StaticSpread
                key={item.key}
                spread={item.spread}
                aspect={getCanvasAspect(formatPreset, item.spread.kind)}
                bookFont={bookFont}
                formatPreset={formatPreset}
                folios={item.folios}
                fallbackTitle={project?.title}
              />
            ) : (
              <SynthesizedPage
                key={item.key}
                kind={item.kind}
                text={item.kind === 'dedication' ? item.text : ''}
                aspect={singleAspect}
                formatPreset={formatPreset}
                bookFont={bookFont}
              />
            )}
          </div>
        </div>
        <button
          type="button"
          className="preview__arrow"
          onClick={() => go(1)}
          disabled={i === items.length - 1}
          aria-label="Next"
        >
          <IconArrowRight />
          <span className="preview__arrow__label">Next</span>
        </button>
      </div>

      <div className="preview__dots">
        {items.map((it, n) => (
          <button
            key={it.key}
            type="button"
            className={`preview__dot ${n === i ? 'preview__dot--on' : ''}`}
            onClick={() => setI(n)}
            title={it.label}
            aria-label={it.label}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * A page the PDF synthesizes (dedication / blank) rendered at the same
 * point-true metrics as buildBookPdf: dedication is 16pt type, 1.42
 * line-height, centered inside pageWidth − 120pt.
 */
function SynthesizedPage({
  kind,
  text,
  aspect,
  formatPreset,
  bookFont,
}: {
  kind: 'dedication' | 'blank';
  text: string;
  aspect: number;
  formatPreset: FormatPreset | null;
  bookFont: BookFont;
}) {
  const { widthPt } = getCanvasSizePt(formatPreset, 'cover');
  const ptCqw = 100 / widthPt;
  return (
    <div className="spread spread--single" style={{ ['--spread-ar' as string]: aspect }}>
      {kind === 'dedication' && (
        <div
          className="preview-page__dedication"
          style={{
            fontFamily: bookFont.family,
            fontWeight: bookFont.weight,
            fontSize: `${16 * ptCqw}cqw`,
            padding: `0 ${(60 / widthPt) * 100}%`,
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

